const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

function scramble(el) {
  const final = el.dataset.final;
  if (!final) return;
  const start = performance.now();
  const scrPhase = 300;
  const lockStep = 80;
  const frame = (now) => {
    const t = now - start;
    const locked = t < scrPhase ? 0 : Math.floor((t - scrPhase) / lockStep) + 1;
    let out = '';
    for (let i = 0; i < final.length; i++) {
      const ch = final[i];
      out += i < locked || !/[0-9]/.test(ch) ? ch : String(Math.floor(Math.random() * 10));
    }
    el.textContent = out;
    if (locked < final.length) requestAnimationFrame(frame);
    else el.textContent = final;
  };
  requestAnimationFrame(frame);
}

export function initHomeFx(root) {
  if (!root) return () => {};
  const q = (s) => Array.from(root.querySelectorAll(s));
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = window.innerWidth < 768;
  const cleanups = [];

  // ── Single reveal observer ──
  const io = new IntersectionObserver((es) => {
    es.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.classList.add('up');
      io.unobserve(e.target);
    });
  }, { threshold: 0.14 });
  q('.reveal,.reveal-l,.reveal-r').forEach((el) => io.observe(el));
  cleanups.push(() => io.disconnect());

  // ── Crisis cells: stagger drift + top bar + scramble ──
  const co = new IntersectionObserver((es) => {
    es.forEach((e) => {
      if (!e.isIntersecting) return;
      const cells = Array.from(e.target.querySelectorAll('.c-cell'));
      cells.forEach((c, i) => setTimeout(() => {
        c.classList.add('vis');
        const n = c.querySelector('[data-final]');
        if (n && !reduced) scramble(n);
      }, i * 100));
      co.unobserve(e.target);
    });
  }, { threshold: 0.25 });
  const cGrid = root.querySelector('.c-grid');
  if (cGrid) co.observe(cGrid);
  cleanups.push(() => co.disconnect());

  // ── Outcomes scramble ──
  const oo = new IntersectionObserver((es) => {
    es.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.querySelectorAll('[data-final]').forEach((n) => { if (!reduced) scramble(n); });
      oo.unobserve(e.target);
    });
  }, { threshold: 0.3 });
  const ocGrid = root.querySelector('.oc-grid');
  if (ocGrid) oo.observe(ocGrid);
  cleanups.push(() => oo.disconnect());

  // ── Journey rail ──
  const rO = new IntersectionObserver((es) => {
    es.forEach((e) => {
      if (!e.isIntersecting) return;
      const fill = root.querySelector('.rail-fill');
      if (fill) fill.classList.add('go');
      q('.r-n').forEach((n, i) => setTimeout(() => n.classList.add('lit'), i * 280 + 200));
      rO.unobserve(e.target);
    });
  }, { threshold: 0.4 });
  const railOuter = root.querySelector('.rail-outer');
  if (railOuter) rO.observe(railOuter);
  cleanups.push(() => rO.disconnect());

  // ── Timeline + journey cards stagger ──
  const stagger = (containerSel, itemSel, step) => {
    const obs = new IntersectionObserver((es) => {
      es.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.querySelectorAll(itemSel).forEach((it, i) => setTimeout(() => it.classList.add('vis'), i * step));
        obs.unobserve(e.target);
      });
    }, { threshold: 0.12 });
    const el = root.querySelector(containerSel);
    if (el) obs.observe(el);
    cleanups.push(() => obs.disconnect());
  };
  stagger('.tl', '.tl-i', 160);
  stagger('.j-cards', '.j-card', 80);

  // ── Unified scroll loop: nav, progress, sticky label, parallax ──
  const nav = root.querySelector('.mkt-nav');
  const progressFill = root.querySelector('[data-progress-fill]');
  const stickyLabel = root.querySelector('[data-sticky-label]');
  const heroVideo = root.querySelector('.hero-video-stack');
  const chipsLayer = root.querySelector('.hero-chips-layer');
  const heroLeft = root.querySelector('.hero-left');
  const driftEls = q('[data-drift]');
  const labeled = q('[data-label]');
  const footer = root.querySelector('footer');
  let currentLabel = '';
  let ticking = false;

  const update = () => {
    ticking = false;
    const y = window.scrollY;
    const vh = window.innerHeight;
    if (nav) nav.classList.toggle('solid', y > 48);
    if (progressFill) {
      const dh = document.documentElement.scrollHeight - vh;
      progressFill.style.width = `${dh > 0 ? (y / dh) * 100 : 0}%`;
    }
    if (!reduced && !isMobile) {
      if (heroVideo && y < vh * 1.2) heroVideo.style.transform = `translateY(${y * 0.5}px)`;
      if (chipsLayer && y < vh * 1.2) chipsLayer.style.transform = `translateY(${y * 0.2}px)`;
      if (heroLeft && y < vh * 1.2) heroLeft.style.transform = `translateY(${y * -0.05}px)`;
      driftEls.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.bottom < -100 || r.top > vh + 100) return;
        const factor = parseFloat(el.dataset.drift) || 0.1;
        const off = clamp((vh / 2 - (r.top + r.height / 2)) * factor, -24, 24);
        el.style.transform = `translateY(${off}px)`;
      });
    }
    if (stickyLabel) {
      let label = '';
      for (const s of labeled) {
        const r = s.getBoundingClientRect();
        if (r.top <= vh / 2 && r.bottom >= vh / 2) { label = s.dataset.label; break; }
      }
      if (footer) {
        const fr = footer.getBoundingClientRect();
        if (fr.top < vh) label = '';
      }
      if (label !== currentLabel) {
        currentLabel = label;
        stickyLabel.style.opacity = '0';
        setTimeout(() => { stickyLabel.textContent = label; stickyLabel.style.opacity = label ? '1' : '0'; }, 180);
      }
    }
  };
  const onScroll = () => {
    if (!ticking) { ticking = true; requestAnimationFrame(update); }
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  update();
  cleanups.push(() => window.removeEventListener('scroll', onScroll));

  // ── Anchor scroll correction: reveal transforms settle mid-scroll on tall
  // mobile layouts, so re-align once the smooth scroll finishes ──
  const onAnchorClick = (e) => {
    const a = e.target.closest('a[href^="#"]');
    if (!a) return;
    const target = root.querySelector(a.getAttribute('href'));
    if (!target) return;
    setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }), 850);
  };
  document.addEventListener('click', onAnchorClick);
  cleanups.push(() => document.removeEventListener('click', onAnchorClick));

  // ── Magnetic buttons ──
  if (!reduced && !isMobile) {
    q('.magnetic').forEach((btn) => {
      const move = (e) => {
        const rect = btn.getBoundingClientRect();
        const x = clamp((e.clientX - rect.left - rect.width / 2) * 0.35, -12, 12);
        const yy = clamp((e.clientY - rect.top - rect.height / 2) * 0.35, -8, 8);
        btn.style.transform = `translate(${x}px, ${yy}px)`;
      };
      const leave = () => { btn.style.transform = 'translate(0,0)'; };
      btn.addEventListener('mousemove', move);
      btn.addEventListener('mouseleave', leave);
      cleanups.push(() => { btn.removeEventListener('mousemove', move); btn.removeEventListener('mouseleave', leave); });
    });

    // ── Tilt cards ──
    q('.tilt').forEach((card) => {
      const move = (e) => {
        const r = card.getBoundingClientRect();
        const rx = clamp(((e.clientY - r.top) / r.height - 0.5) * -12, -6, 6);
        const ry = clamp(((e.clientX - r.left) / r.width - 0.5) * 12, -6, 6);
        card.style.transition = 'transform .15s ease';
        card.style.transform = `perspective(800px) rotateX(${rx}deg) rotateY(${ry}deg)`;
      };
      const leave = () => {
        card.style.transition = 'transform .4s cubic-bezier(.16,1,.3,1)';
        card.style.transform = 'none';
      };
      card.addEventListener('mousemove', move);
      card.addEventListener('mouseleave', leave);
      cleanups.push(() => { card.removeEventListener('mousemove', move); card.removeEventListener('mouseleave', leave); });
    });
  }

  return () => cleanups.forEach((fn) => fn());
}
